import React, {useEffect, useRef} from 'react';
import type {PDFDocumentProxy} from 'pdfjs-dist/legacy/build/pdf.mjs';
import {TextLayer} from 'pdfjs-dist/legacy/build/pdf.mjs';
import {PageViewBox} from './spanMath';
import './pdfjs-text-layer.scss';

interface Props {
    pdf: PDFDocumentProxy;
    pageNumber: number;
    scale: number; // user zoom (1 = 100%)
    width: number; // available container width in px; the page is fit to it
    rotate: number; // ABSOLUTE rotation (intrinsic /Rotate already composed with the user delta by the app)
    onDims: (view: PageViewBox) => void; // unrotated view box, the frame highlight boxes are placed in
    onRendered: () => void; // fires after canvas + text layer are both drawn
    children?: React.ReactNode; // overlay layer
}

export function PdfPage({pdf, pageNumber, scale, width, rotate, onDims, onRendered, children}: Props) {
    const wrapRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const textLayerRef = useRef<HTMLDivElement>(null);
    // call the LATEST callbacks (avoid stale closures without re-running the render effect)
    const onDimsRef = useRef(onDims);
    onDimsRef.current = onDims;
    const onRenderedRef = useRef(onRendered);
    onRenderedRef.current = onRendered;

    useEffect(() => {
        let cancelled = false;
        let renderTask: { promise: Promise<void>; cancel: () => void } | null = null;
        let textLayer: any = null;

        (async () => {
            const page = await pdf.getPage(pageNumber);
            if (cancelled) {
                return;
            }

            // Unrotated user space with the origin, like annotation rects. Not base.width/height: those
            // include the page's /UserUnit.
            const base = page.getViewport({scale: 1, rotation: 0});
            const [x0, y0, x1, y1] = base.viewBox;
            onDimsRef.current({w: x1 - x0, h: y1 - y0, x: x0, y: y0});

            // fit the (rotated) page to the container width, then apply the user zoom
            const natural = page.getViewport({scale: 1, rotation: rotate});
            const fit = width > 0 ? width / natural.width : 1;
            const viewport = page.getViewport({scale: fit * scale, rotation: rotate});
            const dpr = window.devicePixelRatio || 1;

            const wrap = wrapRef.current!;
            wrap.style.width = `${Math.floor(viewport.width)}px`;
            wrap.style.height = `${Math.floor(viewport.height)}px`;
            // pdf.js sizes and positions the text layer from --total-scale-factor, which
            // pdfjs-text-layer.scss derives from these two; without them the text spans mis-scale.
            wrap.style.setProperty('--scale-factor', String(viewport.scale));
            wrap.style.setProperty('--user-unit', String(viewport.userUnit));

            // canvas: buffer at DPR, CSS box at viewport size
            const canvas = canvasRef.current!;
            canvas.width = Math.floor(viewport.width * dpr);
            canvas.height = Math.floor(viewport.height * dpr);
            canvas.style.width = `${Math.floor(viewport.width)}px`;
            canvas.style.height = `${Math.floor(viewport.height)}px`;
            // Requested before render(), so pdf.js gets this context back from getContext. The one it
            // creates itself ({alpha: false, willReadFrequently: true}) anti-aliases text differently.
            // Passing `canvas` instead of `canvas: null` keeps pdf.js's guard against two renders on one canvas.
            canvas.getContext('2d');

            renderTask = page.render({
                canvas,
                viewport,
                transform: dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : undefined,
            });
            try {
                await renderTask.promise;
            } catch (e: any) {
                if (e?.name === 'RenderingCancelledException') {
                    return; // expected on rapid change
                }
                throw e;
            }
            if (cancelled) {
                return;
            }

            // text layer (selectable text over the canvas), same viewport -> aligned
            const container = textLayerRef.current!;
            container.innerHTML = '';
            const textContent = await page.getTextContent();
            if (cancelled) {
                return;
            }
            textLayer = new TextLayer({textContentSource: textContent, container, viewport});
            await textLayer.render();
            if (cancelled) {
                return;
            }

            onRenderedRef.current();
        })().catch((e) => {
            if (!cancelled) {
                console.error('PdfPage render failed', e);
            }
        });

        return () => {
            cancelled = true;
            renderTask?.cancel();
            textLayer?.cancel?.();
        };
    }, [pdf, pageNumber, scale, width, rotate]);

    return (
        <div className="pdfPage" ref={wrapRef}>
            <canvas className="pdfCanvas" ref={canvasRef}/>
            <div className="textLayer" ref={textLayerRef}/>
            {children}
        </div>
    );
}
